import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import { BACKEND_URL } from '../config/api';

const AuthContext = createContext(null);

// Helper to get auth headers (for mobile fallback)
const getAuthHeaders = () => {
  const token = localStorage.getItem('session_token');
  if (token) {
    return { 'Authorization': `Bearer ${token}` };
  }
  return {};
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUserState] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isImpersonating, setIsImpersonating] = useState(false);
  const [impersonationInfo, setImpersonationInfo] = useState(null);

  // Check auth by calling /api/auth/me
  const checkAuth = useCallback(async () => {
    setLoading(true);
    
    try {
      const token = localStorage.getItem('session_token');
      
      // Use XMLHttpRequest for iOS Safari compatibility
      const result = await new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        xhr.open('GET', `${BACKEND_URL}/api/auth/me`, true);
        xhr.setRequestHeader('Accept', 'application/json');
        if (token) xhr.setRequestHeader('Authorization', `Bearer ${token}`);
        xhr.withCredentials = true;
        xhr.timeout = 15000;
        xhr.onload = function () {
          let data;
          try { data = JSON.parse(xhr.responseText); } catch (e) { data = null; }
          resolve({ ok: xhr.status >= 200 && xhr.status < 300, data });
        };
        xhr.onerror = function () { reject(new Error('Network error')); };
        xhr.ontimeout = function () { reject(new Error('Timeout')); };
        xhr.send();
      });

      if (result.ok && result.data) {
        setUserState(result.data);
        // Detect impersonation from response
        if (result.data.is_impersonation) {
          setIsImpersonating(true);
          setImpersonationInfo({
            adminEmail: result.data.impersonation_admin_email,
            adminName: result.data.impersonation_admin_name,
          });
        } else {
          setIsImpersonating(false);
          setImpersonationInfo(null);
        }
        setLoading(false);
        return true;
      } else {
        localStorage.removeItem('session_token');
        localStorage.removeItem('user_data');
        setUserState(null);
        setIsImpersonating(false);
        setImpersonationInfo(null);
        setLoading(false);
        return false;
      }
    } catch (error) {
      // On network error, check if we have cached user data
      const cachedUser = localStorage.getItem('user_data');
      if (cachedUser) {
        try {
          const userData = JSON.parse(cachedUser);
          setUserState(userData);
          setLoading(false);
          return true;
        } catch (e) {
          // Invalid cached data
        }
      }
      
      setUserState(null);
      setLoading(false);
      return false;
    }
  }, []);

  // Run auth check on mount
  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  // Start impersonation — save admin token, switch to impersonation token
  const startImpersonation = useCallback(async (targetUserId) => {
    const currentToken = localStorage.getItem('session_token');

    const res = await fetch(`${BACKEND_URL}/api/admin/impersonate/${targetUserId}`, {
      method: 'POST',
      credentials: 'include',
      headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
    });

    if (!res.ok) {
      const err = await res.clone().json().catch(() => ({ detail: 'Failed to start impersonation' }));
      throw new Error(err.detail || 'Failed to start impersonation');
    }

    const data = await res.clone().json();

    // Save admin's original token
    localStorage.setItem('admin_session_token', currentToken);
    // Switch to impersonation token
    localStorage.setItem('session_token', data.session_token);

    // Refresh user data — will now return the target user with impersonation flags
    await checkAuth();

    return data;
  }, [checkAuth]);

  // End impersonation — restore admin token
  const endImpersonation = useCallback(async () => {
    // Call end endpoint with current impersonation token
    try {
      await fetch(`${BACKEND_URL}/api/admin/impersonate/end`, {
        method: 'POST',
        credentials: 'include',
        headers: getAuthHeaders(),
      });
    } catch (e) {
      // Best-effort — still restore admin token
    }

    // Restore admin token
    const adminToken = localStorage.getItem('admin_session_token');
    if (adminToken) {
      localStorage.setItem('session_token', adminToken);
      localStorage.removeItem('admin_session_token');
    }

    setIsImpersonating(false);
    setImpersonationInfo(null);

    // Refresh to get admin user data back
    await checkAuth();
  }, [checkAuth]);

  const logout = async () => {
    try {
      const token = localStorage.getItem('session_token');
      const xhr = new XMLHttpRequest();
      xhr.open('POST', `${BACKEND_URL}/api/auth/logout`, true);
      if (token) xhr.setRequestHeader('Authorization', `Bearer ${token}`);
      xhr.withCredentials = true;
      xhr.send();
    } catch (error) {
      // Ignore logout errors
    } finally {
      localStorage.removeItem('session_token');
      localStorage.removeItem('user_data');
      localStorage.removeItem('admin_session_token');
      setUserState(null);
      setIsImpersonating(false);
      setImpersonationInfo(null);
    }
  };

  // Refresh user data (e.g., after adding items to update count)
  const refreshUser = async () => {
    try {
      const token = localStorage.getItem('session_token');
      
      const result = await new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        xhr.open('GET', `${BACKEND_URL}/api/auth/me`, true);
        xhr.setRequestHeader('Accept', 'application/json');
        if (token) xhr.setRequestHeader('Authorization', `Bearer ${token}`);
        xhr.withCredentials = true;
        xhr.timeout = 15000;
        xhr.onload = function () {
          let data;
          try { data = JSON.parse(xhr.responseText); } catch (e) { data = null; }
          resolve({ ok: xhr.status >= 200 && xhr.status < 300, data });
        };
        xhr.onerror = function () { reject(new Error('Network error')); };
        xhr.ontimeout = function () { reject(new Error('Timeout')); };
        xhr.send();
      });

      if (result.ok && result.data) {
        setUserState(result.data);
      }
    } catch (error) {
      console.error('Failed to refresh user:', error);
    }
  };

  const value = {
    user,
    loading,
    logout,
    checkAuth,
    refreshUser,
    isAuthenticated: !!user,
    getAuthHeaders,
    // Impersonation
    isImpersonating,
    impersonationInfo,
    startImpersonation,
    endImpersonation,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export { getAuthHeaders };
